import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  ExternalLink,
  CheckCircle2,
  Compass,
  ShieldCheck,
  Building2,
  Layers,
  Sparkles,
  FileText,
  Clock,
  ArrowUpFromLine,
  Maximize2,
  HardHat,
  Truck,
  Weight,
  Ruler,
  DoorOpen,
  Anchor,
  HelpCircle
} from 'lucide-react';
import { COMPANY_INFO, STAFF } from '../data';
import ContactForm from '../components/ContactForm';

export default function Minikran() {
  const getOptimizedUrl = (url, width = 1200) => {
    if (url && url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace(
        'https://assets.auxodev.dk/',
        `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
      );
    }
    return url;
  };

  const heroImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/jekko-minikran-montage-arbejde.jpg";
  const catalogCraneImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/minikran-loeft-materiel-udlejning.jpg";
  const generalCraneImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/kraner.jpg";
  const liftTeaserImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/jlg-bomlift-udlejning.png";
  const minilaesserTeaserImage = "https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/minilaesser-laessemaskine-udlejning.jpg";

  const catalogUrl = "https://vesthimmerlands.brukerweb.no/product/index/";
  const kranerCatalogUrl = "https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=306";

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>Minikran | Vesthimmerlands Materieludlejning</title>
        <meta
          name="description"
          content="Lej en kompakt minikran til tunge præcisionsløft og glasmontering på trange steder. Få rådgivning hos Vesthimmerlands Materieludlejning."
        />
        <meta property="og:title" content="Minikran | Vesthimmerlands Materieludlejning" />
        <meta
          property="og:description"
          content="Lej en kompakt minikran til tunge præcisionsløft og glasmontering på trange steder. Få rådgivning hos Vesthimmerlands Materieludlejning."
        />
        <meta property="og:image" content={heroImage} />
        <link rel="canonical" href="https://vhmu.dk/minikran" />
      </Helmet>

      {/* Hero Header Sektion */}
      <section className="relative overflow-hidden bg-brand-light border-b border-brand-grey pt-12 pb-16 md:pt-16 md:pb-24">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          {/* Brødkrumme */}
          <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-400 mb-6">
            <Link to="/" className="hover:text-brand-yellow transition-colors">
              Forside
            </Link>
            <span>/</span>
            <span className="text-gray-500">Produktkatalog</span>
            <span>/</span>
            <span className="text-brand-yellow">Minikran</span>
          </nav>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Tekstkolonne */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/20 px-3.5 py-1.5 text-xs font-black uppercase tracking-wider text-brand-yellow mb-6">
                <Sparkles className="h-3.5 w-3.5" />
                <span>Kompakt & Præcist Løfteudstyr</span>
              </div>

              <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900 sm:text-4xl md:text-5xl lg:text-5xl leading-[1.1]">
                Lej en minikran, <br className="hidden sm:inline" />
                <span className="text-brand-yellow">når pladsen er trang</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-gray-700 leading-relaxed font-normal">
                Har du et tungt element, der skal på plads, men ikke plads til en stor kran? Hos <Link to="/" className="text-gray-900 font-semibold underline decoration-brand-yellow decoration-2 underline-offset-4 hover:text-brand-yellow transition-colors">Vesthimmerlands Materieludlejning</Link> kan du leje en minikran til opgaver, hvor adgang og præcision spiller en stor rolle. Vi hjælper dig med at vurdere, hvilket materiel der passer til løftet, før du lægger din plan.
              </p>

              {/* Handlingsknapper */}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#kontakt-tilbud"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-brand-yellow px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white shadow-sm hover:bg-brand-yellow-hover transition-all duration-200 cursor-pointer"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <ArrowRight className="h-4 w-4" />
                </a>

                <a
                  href={kranerCatalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-white border border-brand-grey px-5 py-3.5 text-sm font-bold uppercase tracking-wider text-gray-800 shadow-sm hover:border-brand-yellow hover:text-brand-yellow transition-all duration-200"
                >
                  <span>Se kraner i online-katalog</span>
                  <ExternalLink className="h-4 w-4" />
                </a>

                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="inline-flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-brand-yellow px-2 py-2 transition-colors"
                >
                  <Phone className="h-4 w-4 text-brand-yellow" />
                  <span>{COMPANY_INFO.phone}</span>
                </a>
              </div>

              {/* USP Highlights */}
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 pt-8 border-t border-brand-grey/60">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>Kompakt & høj løfteevne</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>Ideel til trange passager</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                  <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>Egne mekanikere & godkendt</span>
                </div>
              </div>
            </div>

            {/* Billedekolonne */}
            <div className="lg:col-span-5">
              <div className="relative mx-auto max-w-lg lg:max-w-none">
                <div className="overflow-hidden rounded-2xl border border-brand-grey bg-white shadow-xl aspect-[4/3]">
                  <img
                    src={getOptimizedUrl(heroImage, 1000)}
                    alt="Jekko minikran i aktion til præcisionsløft og glasmontering hos Vesthimmerlands Materieludlejning"
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>

                {/* Svævende info-badge */}
                <div className="absolute -bottom-6 -left-4 sm:bottom-4 sm:left-4 rounded-xl bg-white/95 backdrop-blur-sm border border-brand-grey p-4 shadow-lg flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow text-white">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="block text-xs font-bold uppercase tracking-wider text-gray-900">
                      Præcisionsløft & Sikkerhed
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      Sikker montering ved begrænset plads
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 1: Hvor kan en minikran gøre arbejdet lettere? */}
      <section className="py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-3xl">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Fleksibel Anvendelse
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Hvor kan en minikran gøre arbejdet lettere?
            </h2>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7 space-y-6 text-base text-gray-700 leading-relaxed">
              <p>
                En minikran kan være relevant ved montage af glas, vinduer og andre bygningsdele på steder med begrænset plads. Det er dog ikke nok, at kranen kan komme ind. Den skal også kunne løfte emnet i den nødvendige højde og afstand under de forhold, der er på arbejdsstedet.
              </p>
              <p>
                Ved arbejde tæt på bygninger er der ofte mange mål at holde styr på. Smalle adgangsveje, døråbninger og pladsen til at stille kranen op kan afgøre, hvilken løsning der er mulig. Fortæl os, hvordan stedet ser ud, så vi kan tale om mulighederne på et oplyst grundlag.
              </p>

              {/* Eksempler på montageopgaver */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="rounded-xl border border-brand-grey bg-brand-light p-5 transition-all duration-200 hover:border-brand-yellow/30 hover:bg-white hover:shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow mb-3">
                    <Maximize2 className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-1">
                    Glas- & Vinduesmontage
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Præcis håndtering af store glaspartier og facadeglas via vakuumsug og kontrollerede spilbevægelser.
                  </p>
                </div>

                <div className="rounded-xl border border-brand-grey bg-brand-light p-5 transition-all duration-200 hover:border-brand-yellow/30 hover:bg-white hover:shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-yellow/10 text-brand-yellow mb-3">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-1">
                    Bygningsdele & Stålmontage
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Løft af stålbjælker, elementer og installationsudstyr tæt op ad facader eller i gårdmiljøer. Skal der samtidig flyttes udgravede masser eller byggematerialer på terrænet, er en af vores kompakte <Link to="/minilaesser" className="text-brand-yellow font-semibold hover:underline">minilæssere</Link> den oplagte makker.
                  </p>
                </div>
              </div>
            </div>

            {/* Hurtig overbliksboks til højre */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border-2 border-brand-yellow/20 bg-brand-light p-6 sm:p-8">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-brand-yellow mb-2">
                  Kompetent Kranrådgivning
                </h3>
                <h4 className="text-xl font-black uppercase tracking-tight text-gray-900 mb-4">
                  Undgå overraskelser på pladsen
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed mb-6">
                  En minikrans styrke er evnen til at navigere gennem smalle passager på bælter og folde støtteben ud på ujævnt underlag. Vi hjælper dig med at kontrollere kranens løftediagram mod byggepladsens faktiske mål.
                </p>

                <div className="space-y-3 border-t border-brand-grey/80 pt-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-gray-700 font-medium">
                      Gennemgang af løftediagrammer og rækkevidde
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-gray-700 font-medium">
                      Beregning af udlæg og frihøjde under udhæng
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-brand-yellow shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-gray-700 font-medium">
                      Hjælp til vurdering af underlag og understøtning
                    </span>
                  </div>
                </div>

                <div className="mt-8">
                  <a
                    href="#kontakt-tilbud"
                    className="block w-full text-center rounded-lg bg-brand-yellow py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors"
                  >
                    Få teknisk vejledning
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 2: Beskriv løftet, før du vælger kran (De 4 bullets) */}
      <section className="py-20 md:py-28 bg-brand-light border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Forberedelse & Tjekliste
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Beskriv løftet, før du vælger kran
            </h2>
            <p className="mt-4 text-base text-gray-700 leading-relaxed">
              Når du kontakter os om leje af minikran, er det en hjælp at kende vægten og målene på det, der skal løftes. Oplys også, hvor højt og hvor langt emnet skal flyttes. Så har vi et bedre grundlag for at vurdere, om en af vores minikraner kan bruges til opgaven.
            </p>
          </div>

          {/* De 4 afklarende punkter som kreative kort */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Punkt 1 */}
            <div className="rounded-2xl border border-brand-grey bg-white p-6 shadow-sm hover:border-brand-yellow hover:shadow-md transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow">
                    <Weight className="h-6 w-6" />
                  </div>
                  <span className="text-2xl font-black text-gray-200">01</span>
                </div>
                <h3 className="text-base font-bold uppercase tracking-tight text-gray-900 mb-2">
                  Emnets vægt og mål
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Emnets vægt og mål, så løftet kan vurderes ud fra det konkrete materiale.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 text-xs font-semibold text-gray-500">
                F.eks. kg, længde, bredde & tykkelse
              </div>
            </div>

            {/* Punkt 2 */}
            <div className="rounded-2xl border border-brand-grey bg-white p-6 shadow-sm hover:border-brand-yellow hover:shadow-md transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow">
                    <Ruler className="h-6 w-6" />
                  </div>
                  <span className="text-2xl font-black text-gray-200">02</span>
                </div>
                <h3 className="text-base font-bold uppercase tracking-tight text-gray-900 mb-2">
                  Højde & rækkevidde
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Løftehøjde og vandret rækkevidde frem til emnets endelige placering.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 text-xs font-semibold text-gray-500">
                Lodret højde & vandret udlæg i meter
              </div>
            </div>

            {/* Punkt 3 */}
            <div className="rounded-2xl border border-brand-grey bg-white p-6 shadow-sm hover:border-brand-yellow hover:shadow-md transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow">
                    <DoorOpen className="h-6 w-6" />
                  </div>
                  <span className="text-2xl font-black text-gray-200">03</span>
                </div>
                <h3 className="text-base font-bold uppercase tracking-tight text-gray-900 mb-2">
                  Adgangsvej & dørmål
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Adgangsvej, dørmål og pladsen, hvor kranen skal stå.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 text-xs font-semibold text-gray-500">
                Portmål, passager & opstillingsareal
              </div>
            </div>

            {/* Punkt 4 */}
            <div className="rounded-2xl border border-brand-grey bg-white p-6 shadow-sm hover:border-brand-yellow hover:shadow-md transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow">
                    <Layers className="h-6 w-6" />
                  </div>
                  <span className="text-2xl font-black text-gray-200">04</span>
                </div>
                <h3 className="text-base font-bold uppercase tracking-tight text-gray-900 mb-2">
                  Underlagets beskaffenhed
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Underlagets beskaffenhed og forhold, der kan påvirke opstillingen.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 text-xs font-semibold text-gray-500">
                Bæreevne, kælderdæk eller blød jord
              </div>
            </div>
          </div>

          {/* Underlagsfokus / uddybende tekst */}
          <div className="mt-12 rounded-2xl bg-white border border-brand-grey p-6 sm:p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-yellow/10 text-brand-yellow shrink-0">
              <Anchor className="h-7 w-7" />
            </div>
            <div className="flex-grow text-sm sm:text-base text-gray-700 leading-relaxed">
              <p>
                <strong>Vigtigt om underlag:</strong> Underlaget er især værd at undersøge, hvis arbejdet foregår indendørs eller på et areal med begrænset bæreevne. En minikran fylder mindre end mange større kraner, men det betyder ikke, at den passer alle steder. Vi hjælper dig med at få de vigtige forhold afklaret inden leje.
              </p>
            </div>
            <div className="shrink-0 w-full md:w-auto">
              <a
                href="#kontakt-tilbud"
                className="inline-flex w-full md:w-auto items-center justify-center gap-2 rounded-md bg-brand-light border border-brand-grey px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-900 hover:border-brand-yellow hover:text-brand-yellow transition-colors"
              >
                <span>Afklar dit løft med os</span>
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 3: Minikran udlejning begynder med din opgave & kranudvalg */}
      <section className="py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Tekst */}
            <div className="lg:col-span-6 space-y-6">
              <div>
                <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
                  Personlig Rådgivning & Løftegrej
                </span>
                <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
                  Minikran udlejning begynder med din opgave
                </h2>
              </div>

              <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                <p>
                  Hos os begynder udlejning af minikran med en samtale om løftet, arbejdsstedet og din tidsplan. Du får rådgivning om valg af materiel frem for at skulle gætte dig frem. Vores egne mekanikere vedligeholder maskinerne løbende, og vi tilbyder fleksible lejeaftaler.
                </p>
                <p>
                  Skal kranen bruges til montage, må du også gerne fortælle, om emnet kræver særligt løftegrej. Behovet afhænger af materialet og den konkrete opgave. Det er bedst at afklare de detaljer, før du beslutter, hvilket materiel du vil leje, og hvordan arbejdet skal planlægges.
                </p>
              </div>

              <div className="pt-4 flex flex-wrap gap-4">
                <a
                  href={kranerCatalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-brand-yellow px-5 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors"
                >
                  <span>Åbn kraner i varekatalog</span>
                  <ExternalLink className="h-4 w-4" />
                </a>

                <a
                  href="#kontakt-tilbud"
                  className="inline-flex items-center gap-2 rounded-md bg-white border border-brand-grey px-5 py-3 text-sm font-bold uppercase tracking-wider text-gray-800 hover:border-brand-yellow hover:text-brand-yellow transition-colors"
                >
                  <span>Spørg om særligt løftegrej</span>
                </a>
              </div>
            </div>

            {/* Billede / Præsentationskort */}
            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-brand-grey bg-white p-4 sm:p-6 shadow-lg">
                <div className="overflow-hidden rounded-xl aspect-[16/10] bg-gray-100 mb-6">
                  <img
                    src={getOptimizedUrl(catalogCraneImage, 800)}
                    alt="Minikraner og løftemateriel serviceret og klar til udlejning"
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-yellow">
                      Vesthimmerlands Materieludlejning
                    </span>
                    <h3 className="text-lg font-black uppercase text-gray-900">
                      Kraner & Løfteløsninger
                    </h3>
                    <p className="text-xs text-gray-500">
                      Gennemgået, godkendt og serviceret af egne mekanikere
                    </p>
                  </div>
                  <a
                    href={kranerCatalogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-light border border-brand-grey px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-900 hover:border-brand-yellow hover:text-brand-yellow transition-colors shrink-0"
                  >
                    <span>Se katalogdata</span>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 4: Minikran leje, hvad bestemmer prisen? */}
      <section className="py-20 md:py-28 bg-brand-light border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-3xl mb-14">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Pris & Betingelser
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Minikran leje, hvad bestemmer prisen?
            </h2>
            <p className="mt-4 text-base text-gray-700 leading-relaxed">
              Vil du kende prisen på en minikran til leje, har vi brug for flere oplysninger end antallet af dage. Valg af materiel, lejeperiodens længde og de praktiske forhold omkring opgaven kan have betydning. Derfor tager vi udgangspunkt i dit løft i stedet for at angive en standardpris.
            </p>
          </div>

          {/* 3 Prisfaktorer */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow mb-6">
                  <HardHat className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-black uppercase tracking-tight text-gray-900 mb-3">
                  Valg af materiel & grej
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Kranens kapacitet, rækkevidde samt eventuelt behov for specialløftegrej som vakuumsug til glas eller pallegafler.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-2 text-xs font-bold text-gray-500">
                <CheckCircle2 className="h-4 w-4 text-brand-yellow" />
                <span>Præcis tilpasning til emnet</span>
              </div>
            </div>

            <div className="rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow mb-6">
                  <Clock className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-black uppercase tracking-tight text-gray-900 mb-3">
                  Lejeperiodens længde
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Uanset om du skal bruge minikranen til et enkelt præcisionsløft på få timer, en hel uge eller et længerevarende montageprojekt.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-2 text-xs font-bold text-gray-500">
                <CheckCircle2 className="h-4 w-4 text-brand-yellow" />
                <span>Fleksible dags- & ugeaftaler</span>
              </div>
            </div>

            <div className="rounded-2xl border border-brand-grey bg-white p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-yellow/10 text-brand-yellow mb-6">
                  <Truck className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-black uppercase tracking-tight text-gray-900 mb-3">
                  Praktiske forhold & transport
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Arbejdsstedets adresse, adgangsforhold og om arbejdet foregår inde eller ude. Vi leverer hurtigt direkte til din byggeplads.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-2 text-xs font-bold text-gray-500">
                <CheckCircle2 className="h-4 w-4 text-brand-yellow" />
                <span>Levering med egne biler</span>
              </div>
            </div>
          </div>

          {/* Oplysninger til tilbud */}
          <div className="mt-8 rounded-2xl bg-white border border-brand-grey p-6 sm:p-8">
            <h3 className="text-base font-bold uppercase tracking-tight text-gray-900 mb-2">
              Hvad kan du have klar, når du kontakter os?
            </h3>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
              Har du en dato og en adresse for opgaven, så tag dem med, når du kontakter os. Fortæl også, om arbejdet foregår inde eller ude, og om noget påvirker adgangen. Så kan vi gennemgå mulighederne og give dig et uforpligtende tilbud på leje af minikran.
            </p>
          </div>
        </div>
      </section>

      {/* Sektion 5: Leje af minikran til opgaver i Aalborg og Aarhus */}
      <section className="py-20 md:py-28 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Geografisk Rækkevidde
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-gray-900">
              Leje af minikran til opgaver i Aalborg og Aarhus
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7 space-y-6 text-base text-gray-700 leading-relaxed">
              <p>
                Vi har afdelinger i Svenstrup ved Aalborg og i Løgstør. Vi hjælper kunder med materiel til byggeri, montage og entreprenørarbejde i Nordjylland. Skal du bruge en minikran i Aalborg eller til en opgave omkring Aarhus, tager vi en snak om behovet og de praktiske muligheder. Se også vores <Link to="/maskinudlejning" className="text-brand-yellow font-semibold hover:underline">maskinudlejning</Link> og <Link to="/entreprenormateriel" className="text-brand-yellow font-semibold hover:underline">entreprenørmateriel</Link> til byggepladsen.
              </p>
              <p>
                Et godt lejeforløb starter også med at få styr på tidspunktet for løftet og på, hvordan materiellet skal frem til arbejdsstedet. Jo tidligere du fortæller os om adgangsforhold og tidsplan, desto bedre kan vi afklare mulighederne sammen med dig og finde en lejeaftale, der passer til opgaven.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="rounded-xl border border-brand-grey bg-brand-light p-4">
                  <div className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-1">
                    <MapPin className="h-4 w-4 text-brand-yellow" />
                    <span>Aalborg & Nordjylland</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Hurtig udkørsel fra vores base i Svenstrup J til hele Aalborg og omegn.
                  </p>
                </div>

                <div className="rounded-xl border border-brand-grey bg-brand-light p-4">
                  <div className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-1">
                    <MapPin className="h-4 w-4 text-brand-yellow" />
                    <span>Aarhus & Midtjylland</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Fleksibel levering og rådgivning til bygge- og montageprojekter i og omkring Aarhus.
                  </p>
                </div>
              </div>
            </div>

            {/* Lokationskort */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border-2 border-brand-grey bg-brand-light p-6 sm:p-8">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-brand-yellow mb-2">
                  Vores Afdelinger
                </h3>
                <h4 className="text-xl font-black uppercase tracking-tight text-gray-900 mb-6">
                  Tæt på dit byggeprojekt
                </h4>

                <div className="space-y-6">
                  {/* Svenstrup */}
                  <div className="rounded-xl bg-white border border-brand-grey p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-brand-yellow">
                        Hovedafdeling
                      </span>
                      <span className="text-xs font-semibold text-gray-400">9230 Svenstrup J</span>
                    </div>
                    <h5 className="text-base font-bold text-gray-900">{COMPANY_INFO.address}</h5>
                    <p className="text-xs text-gray-500 mt-1">
                      Perfekt placering for hurtig betjening af hele Aalborg, Nordjylland og E45.
                    </p>
                  </div>

                  {/* Løgstør */}
                  <div className="rounded-xl bg-white border border-brand-grey p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-brand-yellow">
                        Afdeling
                      </span>
                      <span className="text-xs font-semibold text-gray-400">9670 Løgstør</span>
                    </div>
                    <h5 className="text-base font-bold text-gray-900">Bredgade 52, 9670 Løgstør</h5>
                    <p className="text-xs text-gray-500 mt-1">
                      Lokal forankring og bredt udvalg af materiel til Vesthimmerland og omegn.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 6: Relateret materiel til byggepladsen (Krydshenvisninger til /liftudlejning og /minilaesser) */}
      <section className="py-16 md:py-20 bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-yellow/20 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-yellow">
              Kombiner Dit Materiel
            </span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-white">
              Relateret materiel til din byggeplads
            </h2>
            <p className="mt-3 text-sm sm:text-base text-gray-300">
              Få samlet hele dit maskinbehov hos os – fra personløft i højden til jordflytning på terrænet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Personlift kort */}
            <div className="rounded-3xl border border-gray-800 bg-gray-800/80 p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-brand-yellow">
                  Personløft i Højden
                </span>
                <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white mt-1 mb-3">
                  Skal du også bruge en lift til montagen?
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed mb-6">
                  En minikran er beregnet til at løfte materialer, ikke personer. Skal dit hold arbejde i højden under montagen, kan en lift være relevant ved siden af kranen. Se vores brede sortiment af sakselifte og bomlifte.
                </p>
              </div>

              <div>
                <div className="overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 aspect-16/9 mb-6 shadow-xl">
                  <img
                    src={getOptimizedUrl(liftTeaserImage, 600)}
                    alt="Bomlift til sikkert arbejde i højden i kombination med kranmontage"
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/liftudlejning"
                    className="inline-flex items-center gap-2 rounded-md bg-brand-yellow px-5 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors"
                  >
                    <span>Se vores liftudlejning</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <a
                    href="#kontakt-tilbud"
                    className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors"
                  >
                    <span>Samlet tilbud</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Minilæsser kort */}
            <div className="rounded-3xl border border-gray-800 bg-gray-800/80 p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-brand-yellow">
                  Flytning af Jord & Materialer
                </span>
                <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white mt-1 mb-3">
                  Kompakte minilæssere til trange arbejdssteder
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed mb-6">
                  Skal du have flyttet udgravede materialer, grus eller paller rundt på arbejdsstedet, hvor en stor læsser fylder for meget? Vores minilæssere er smidige, stærke og klar til opgaven.
                </p>
              </div>

              <div>
                <div className="overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 aspect-16/9 mb-6 shadow-xl">
                  <img
                    src={getOptimizedUrl(minilaesserTeaserImage, 600)}
                    alt="Kompakt minilæsser til flytning af jord, grus og materialer på byggepladsen"
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/minilaesser"
                    className="inline-flex items-center gap-2 rounded-md bg-brand-yellow px-5 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-colors"
                  >
                    <span>Se vores minilæssere</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to="/maskinudlejning"
                    className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors"
                  >
                    <span>Øvrige maskiner</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 7: Rådgivning & Uforpligtende Tilbudsformular (Afsluttende SEO-afsnit) */}
      <section id="kontakt-tilbud" className="py-20 md:py-28 bg-white scroll-mt-10">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Venstre side: Rådgivning & Kontaktinfo */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
                  Personlig Rådgivning
                </span>
                <h2 className="mt-3 text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900 leading-tight">
                  Vil du høre, hvilken minikran der passer?
                </h2>
              </div>

              <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                <p>
                  Vil du høre, hvilken minikran der kan passe til dit løft? Du er velkommen til at <Link to="/kontakt" className="text-brand-yellow font-semibold hover:underline">kontakte os</Link> med oplysninger om emnet, arbejdsstedet og den forventede lejeperiode. Så kan vi rådgive dig ud fra den konkrete opgave og give dig et uforpligtende tilbud.
                </p>
                <p className="text-sm text-gray-600">
                  Er du erhvervsdrivende og ønsker faste fordele? Opret nemt en konto via vores <Link to="/bliv-kunde" className="text-brand-yellow font-semibold hover:underline">erhvervskundeoprettelse</Link>.
                </p>
              </div>

              {/* Direkte udlejere med billede og telefon */}
              <div className="pt-4 border-t border-brand-grey space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Tal direkte med vores udlejere
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {STAFF.map((member, idx) => (
                    <div
                      key={idx}
                      className="rounded-xl border border-brand-grey bg-brand-light p-4 flex items-center gap-3"
                    >
                      <img
                        src={getOptimizedUrl(member.image, 160)}
                        alt={`${member.name} - ${member.role} hos Vesthimmerlands Materieludlejning`}
                        className="h-12 w-12 rounded-full object-cover border border-brand-grey"
                      />
                      <div className="min-w-0">
                        <span className="block text-xs font-bold uppercase text-gray-900 truncate">
                          {member.name}
                        </span>
                        <span className="block text-[11px] text-gray-500 truncate">
                          {member.role}
                        </span>
                        <a
                          href={`tel:${member.phone.replace(/\s+/g, '')}`}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-brand-yellow hover:underline"
                        >
                          <Phone className="h-3 w-3" />
                          <span>{member.phone}</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hovedkontor Info */}
              <div className="rounded-xl border border-brand-grey bg-brand-dark p-5 space-y-3 text-sm">
                <div className="flex items-center gap-3 text-gray-700">
                  <Phone className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span className="font-bold">{COMPANY_INFO.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Mail className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span className="font-bold">{COMPANY_INFO.email}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <MapPin className="h-4 w-4 text-brand-yellow shrink-0" />
                  <span>{COMPANY_INFO.address}</span>
                </div>
              </div>
            </div>

            {/* Højre side: Formular */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-brand-grey bg-brand-light p-6 sm:p-8 md:p-10 shadow-sm">
                <div className="mb-6">
                  <h3 className="text-xl font-black uppercase tracking-tight text-gray-900">
                    Få et uforpligtende tilbud på minikran
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Oplys gerne emnets vægt/mål, højde, rækkevidde og arbejdssted, så vi hurtigt kan rådgive dig.
                  </p>
                </div>

                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
